import { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import {
  Button,
  Input,
  TruncateText,
  classNames,
  noop,
  useModal,
} from '@panwds/react-ui'
import { CaretRightIcon, SearchIcon, PlusCircleIcon, RemoveIcon } from '@panwds/icons'
import Tree from 'rc-tree'
import { SUPER_ROOT_ID } from '../../../activation/constants'
import { SEARCH_TENANTS } from '../../../common/constants/LabelConstants'
import { searchTenant, TreeSearch } from '../../../common/components/page/sidebar/TreeSearch'
import TenantCreationModal from './TenantCreationModal'
import '../../../common/components/page/rcTree/RCTree.scss'

import type { VFC, ReactNode, MouseEvent } from 'react'
import type { Tsg, ReadonlyTsgTree } from '@sparky/framework'
import type { FieldDataNode, TreeProps } from 'rc-tree'

type TsgTreeDataNode = FieldDataNode<{
  key: string
  title?: ReactNode
}>

export type ActionRenderParams = { tsg: Tsg, level: number }
export type RenderActionsFn = (param: ActionRenderParams) => ReactNode[] | ReactNode | undefined

export type TSGTreeProps = {
  tsg: Tsg | undefined
  tsgs: ReadonlyTsgTree
  onSelect: (tsgId: string) => void
  selectedTsgId: string | undefined
  isVisible: boolean
  hasAddBtn?: boolean
  rootAddBtn?: boolean
  showTsgIdInTree?: boolean
  renderActions?: RenderActionsFn
  onClickAddBtn?: () => void
  showSuperRoot?: boolean
  addTreeClassName?: string
  addSearchClassName?: string
  addSearchInputClassName?: string
  lockedTsgIds?: string[]
  hoverToShowInstanceTable?: boolean
  setHoveringTsgId?: (tsgId: string) => void
}
/**
 * @param lockedTsgIdsSet a set that contain all the locked tsgIds
 * @param tsgId target tsgId to check
 * @returns whether need to disable this tsg
 */
const disableTsg = (lockedTsgIdsSet: Set<string>, tsgId: string): boolean => {
  if (!lockedTsgIdsSet.size) {
    return false
  }
  return !lockedTsgIdsSet.has(tsgId)
}

const AddBtn: VFC<{ tsgInfo: Tsg, onCreatedTsg: (tsg: Tsg) => void, onClick?: () => void }> = ({ tsgInfo, onCreatedTsg, onClick = noop }) => {
  const tenantCreationModal = useModal(TenantCreationModal)
  // return null
  return (<>
    <Button
      addClassName='context-btn cm-rounded-full'
      appearance='clear'
      aria-label='add-icon'
      size='sm'
      icon={<PlusCircleIcon size='sm' className='cm-text-gray-600' />}
      onClick={(e: MouseEvent) => {
        // this will prevent selection of TSG on the current row
        e.stopPropagation()
        tenantCreationModal.openModal({ selectedTsg: tsgInfo, onCreatedTsg })
        // close popover
        onClick()
      }}
    />
  </>)
}

const TSGTree: VFC<TSGTreeProps> = (props) => {
  const {
    tsg,
    tsgs,
    onSelect: _onSelect,
    selectedTsgId,
    showTsgIdInTree,
    isVisible,
    hasAddBtn,
    rootAddBtn,
    onClickAddBtn,
    renderActions,
    addTreeClassName,
    addSearchClassName,
    addSearchInputClassName,
    showSuperRoot = false,
    lockedTsgIds,
    hoverToShowInstanceTable: hoverToShowTable = false,
    setHoveringTsgId: setOutsideTsgId = noop
  } = props
  const treeRef = useRef<HTMLDivElement>(null)
  const [hoveringTsgId, setInsideId] = useState<string>('')
  const lockedTsgIdsSet = useMemo<Set<string>>(() => {
    return new Set(lockedTsgIds)
  }, [lockedTsgIds])

  const onCreatedTsg = useCallback((tsg: Tsg) => {
    _onSelect(tsg.id)
  }, [_onSelect])

  useEffect(() => {
    if (hoveringTsgId === '') {
      return setOutsideTsgId(hoveringTsgId)
    }
    else {
      const setId = setTimeout(() => {
        setOutsideTsgId(hoveringTsgId)
      }, 300)
      return () => clearTimeout(setId)
    }
  }, [setOutsideTsgId, hoveringTsgId])

  const getTitle = useCallback((each: Tsg, level: number, showTsgId = false) => {
    const actions = renderActions?.({ tsg: each, level })
    const actionsWidth = actions ? 1.5 : 0 // temp hardcode
    const text = (
      <TruncateText style={{ maxWidth: `${Math.max(16.5 - level - actionsWidth, 5)}rem`, display: 'inline-block' }}
        dataTestId={`tsg-title:${each.display_name}`}>
        {each.display_name} {showTsgId && <small className='cm-opacity-50'>- {each.id}</small>}
      </TruncateText>
    )
    if (hoverToShowTable && each.id !== SUPER_ROOT_ID) {
      return (
        <div className='cm-flex cm-justify-between' onMouseEnter={() => {
          setInsideId(each.id)
        }} onMouseLeave={() => setInsideId('')}>
          {text}
          {actions}
          {hasAddBtn && (hoveringTsgId === each.id) && <AddBtn tsgInfo={each} onCreatedTsg={onCreatedTsg} onClick={onClickAddBtn} />}
        </div>
      )
    }
    else {
      return <div className='cm-flex cm-justify-between'>
        {text}
        {actions}
        {rootAddBtn && <AddBtn tsgInfo={each} onCreatedTsg={onCreatedTsg} onClick={onClickAddBtn} />}
      </div>
    }
  }, [hasAddBtn, hoverToShowTable, hoveringTsgId, onClickAddBtn, onCreatedTsg, rootAddBtn, renderActions])

  const paddingRight = renderActions ? '0.25rem' : undefined
  const getTreeData = useCallback((children?: Tsg[], level = 0, parentHasDupName = showTsgIdInTree ?? false): TsgTreeDataNode[] => {
    const hasDupName = parentHasDupName ||
      (children && children.length > 1 ? new Set(children?.map(each => each.display_name)).size > 1 : false)
    return children?.map((each: Tsg) => ({
      key: each.id,
      style: { width: 'inherit', minWidth: '250px', paddingRight },
      title: getTitle(each, level, showTsgIdInTree ?? hasDupName),
      // className: 'cm-rounded',
      children: each.children ? getTreeData(each.children, level + 1, hasDupName) : undefined,
      isLeaf: !each.children,
      'data-tsgid': each.id,
      'data-testid': `tsg-id:${each.id}`,
      disabled: each.id === SUPER_ROOT_ID || disableTsg(lockedTsgIdsSet, each.id),
    }) as TsgTreeDataNode) || []
  }, [getTitle, lockedTsgIdsSet, paddingRight, showTsgIdInTree])

  const tsgId = tsg?.id
  const [flattenTree, treeData] = useMemo(() => [tsgs.list, getTreeData(showSuperRoot ? tsgs : tsgs.children)], [getTreeData, showSuperRoot, tsgs])
  const [searchInput, setSearchInput] = useState<string>('')
  const [expandedKeys, setExpandedKeys] = useState<string[]>(() => {
    if (!tsgs.children?.length) {
      return []
    }
    const parentId = tsgId && tsgs.get(tsgId)?.parent_id
    const initExpanded: string[] = parentId ? [parentId] : [SUPER_ROOT_ID]
    if (tsgs.children?.length > 10) {
      return initExpanded
    }
    const nodes = flattenTree.length < 10 ? flattenTree : tsgs.children
    return nodes.reduce((tsgIds, tsg) => {
      if (tsg.children && tsg.id !== parentId) {
        tsgIds.push(tsg.id)
      }
      return tsgIds
    }, initExpanded)
  })

  const selected = useMemo<string[]>(() => (selectedTsgId ? [selectedTsgId] : []), [selectedTsgId])

  const onExpand = useCallback((expandedKeys, { node }) => {
    if (node.key === SUPER_ROOT_ID) {
      // for superRoot, don't toggle expand
      return
    }
    setExpandedKeys(expandedKeys)
  }, [setExpandedKeys])


  const expandedKeysRef = useRef(expandedKeys)
  expandedKeysRef.current = expandedKeys

  const onSelect: TreeProps['onSelect'] = useCallback((selectedKeys, e) => {
    _onSelect(e.node.key)
  }, [_onSelect])

  const onResultClick = useCallback((tenant: Tsg) => {
    if (tenant.parent_id && tsgs.has(tenant.parent_id)) {
      setExpandedKeys([tenant.parent_id, ...expandedKeysRef.current])
    }
    _onSelect(tenant.id)
    setSearchInput('')
  }, [_onSelect, tsgs])

  const scrollIntoView = useCallback((tsgId: string) => {
    const foundEl = treeRef.current?.querySelector(`.tenant-tree-treenode[data-tsgid="${tsgId}"]`)
    if (foundEl) {
      if ('scrollIntoViewIfNeeded' in foundEl) {
        (foundEl as any).scrollIntoViewIfNeeded(true)
      }
      else {
        foundEl.scrollIntoView?.({ inline: 'start', block: 'center' })
      }
    }
  }, [])

  useEffect(() => {
    if (isVisible && selectedTsgId && treeRef.current && !treeRef.current.scrollTop) {
      scrollIntoView(selectedTsgId)
    }
  }, [isVisible, scrollIntoView, selectedTsgId])

  const searchResults = useMemo(() => {
    return !searchInput?.trim().length ? [] : searchTenant(searchInput, tsgs.superRoot)
  }, [searchInput, tsgs])
  const getSwitcherIcon = useCallback((arg) => {
    if (arg?.isLeaf) {
      return null
    }
    const className = classNames('cm-transition-transform cm-mb-[-2px]', arg?.expanded && 'cm-rotate-90')
    return <CaretRightIcon size='xxs' className={className} />
  }, [])

  const tree = useMemo(() => {
    if (searchInput) {
      return null
    }
    return <Tree
      prefixCls='tenant-tree'
      showIcon={false}
      selectedKeys={selected}
      expandedKeys={expandedKeys}
      switcherIcon={getSwitcherIcon}
      onSelect={onSelect}
      onExpand={onExpand}
      autoExpandParent={true}
      treeData={treeData}
    />
  }, [expandedKeys, getSwitcherIcon, onExpand, onSelect, searchInput, selected, treeData])

  return (
    <>
      <div className={classNames('cm-items-center cm-p-3', addSearchClassName)}>
        <Input
          addClassName={classNames('cm-w-full', addSearchInputClassName)}
          prefix={<SearchIcon size='sm' className='cm-text-gray-300' />}
          suffix={
            <Button
              aria-label='clear'
              appearance='tertiary-clear'
              size='sm'
              onClick={() => setSearchInput('')}
              icon={<RemoveIcon size='sm' className='cm-text-gray-300' />}
              addClassName={searchInput ? '' : 'cm-hidden'}
            />
          }
          placeholder={SEARCH_TENANTS}
          value={searchInput}
          onChange={event => setSearchInput(event?.target?.value)}
        />
      </div>
      <div ref={treeRef} className={classNames('cm-flex-1 cm-overflow-auto cm-px-3 cm-pb-3 tw-scrollbar-default dark:tw-scrollbar-track-dark dark:tw-scrollbar-thumb-dark dark:tw-scrollbar-corner-dark', addTreeClassName)}>
        {searchInput ? (
          <TreeSearch searchInput={searchInput} searchResults={searchResults} onResultClick={onResultClick} setOutsideId={setInsideId} />
        ) : tree}
      </div>
    </>
  )
}

export default TSGTree
